/**
 * White-glove intake: estimated system count, platforms, contact.
 * Plan: billing_infrastructure_plan §1.8. Submit → /onboarding/white-glove/billing.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  WHITE_GLOVE_PLATFORMS,
  validateWhiteGloveIntake,
  type WhiteGloveIntakeData,
  type WhiteGloveIntakeErrors,
} from '@/lib/white-glove';

export default function WhiteGloveIntake() {
  const navigate = useNavigate();
  const [estimatedSystemCount, setEstimatedSystemCount] = useState<string>('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<WhiteGloveIntakeErrors>({});

  const handlePlatformChange = (id: string, checked: boolean) => {
    setPlatforms((prev) =>
      checked ? [...prev, id] : prev.filter((p) => p !== id)
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: WhiteGloveIntakeData = {
      estimatedSystemCount: parseInt(estimatedSystemCount, 10) || 0,
      platforms,
      email: email.trim(),
      phone: phone.trim(),
    };
    const validation = validateWhiteGloveIntake(data);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    navigate('/app/onboarding/white-glove/billing', {
      state: { whiteGloveIntake: data },
    });
  };

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <CardTitle>White-glove setup</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="system-count">Estimated system count</Label>
            <Input
              id="system-count"
              data-testid="input-system-count"
              type="number"
              min={1}
              value={estimatedSystemCount}
              onChange={(e) => setEstimatedSystemCount(e.target.value)}
            />
            {errors.estimatedSystemCount && (
              <p className="text-sm text-destructive" data-testid="error-system-count">
                {errors.estimatedSystemCount}
              </p>
            )}
          </div>
          <div>
            <Label>Platforms</Label>
            <div className="flex flex-col gap-2 mt-1">
              {WHITE_GLOVE_PLATFORMS.map(({ id, label }) => (
                <label key={id} className="flex items-center gap-2">
                  <Checkbox
                    data-testid={`checkbox-platform-${id}`}
                    checked={platforms.includes(id)}
                    onCheckedChange={(checked) =>
                      handlePlatformChange(id, checked === true)
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            {errors.platforms && (
              <p className="text-sm text-destructive" data-testid="error-platforms">
                {errors.platforms}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="input-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && (
              <p className="text-sm text-destructive" data-testid="error-email">
                {errors.email}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="phone">Phone (optional)</Label>
            <Input
              id="phone"
              data-testid="input-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <Button type="submit" data-testid="btn-intake-submit">
            Continue
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
